import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { leadsAPI, usersAPI, emailCampaignsAPI, emailGeneratorAPI } from '../../services/api';
import EmailHistoryModal from './EmailHistoryModal';

const Modal = ({ isOpen, children, onClose }) =>
  isOpen ? (
    <div className="fixed z-40 left-0 top-0 w-full h-full flex items-center justify-center bg-black bg-opacity-30">
      <div className="bg-white rounded-lg p-6 shadow-xl w-[90vw] max-w-md relative">
        {children}
        <button
          onClick={onClose}
          className="absolute top-2 right-3 text-gray-500 hover:text-black text-xl"
        >
          ×
        </button>
      </div>
    </div>
  ) : null;

function EmailCampaign({ role }) {
  const { token } = useContext(AuthContext);
  const userId = localStorage.getItem('id');
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [newCampaign, setNewCampaign] = useState({
    leadId: '',
    category: 'Promotional',
    subject: '',
    body: '',
    sentBy: '',
    sentAt: ''
  });
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalCategory, setModalCategory] = useState('');

  // AI modal states
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [aiLoading, setAILoading] = useState(false);
  const [aiSubject, setAISubject] = useState('');
  const [aiBody, setAIBody] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadsResponse, usersResponse] = await Promise.all([
          role === 'admin' ? leadsAPI.getAllLeads() : leadsAPI.getAssignedLeads(userId),
          role === 'admin' ? usersAPI.getAllUsers() : usersAPI.getCurrentUser(),
        ]);
        const fetchedLeads = leadsResponse.data.leads || leadsResponse.data || [];
        setLeads(fetchedLeads);
        const fetchedUsers = usersResponse.data.users || [];
        setUsers(fetchedUsers);
        if (fetchedLeads.length > 0) {
          setNewCampaign({
            ...newCampaign,
            leadId: fetchedLeads[0]._id,
            sentBy: role === 'admin' ? fetchedUsers[0]?._id || userId : userId,
          });
        } else if (role !== 'admin') {
          setError('No assigned leads found');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch data');
      }
    };
    fetchData();
    // eslint-disable-next-line
  }, [token, userId, role]);

  const handleAddCampaign = async () => {
    if (!newCampaign.leadId) {
      setError('Please select a lead');
      return;
    }
    if (!newCampaign.sentBy) {
      setError('Please select a user');
      return;
    }
    if (!newCampaign.sentAt) {
      setError('Please select a date and time');
      return;
    }
    try {
      const response = await emailCampaignsAPI.addEmailCampaign(newCampaign.leadId, {
        category: newCampaign.category,
        subject: newCampaign.subject,
        content: newCampaign.body,
        sentBy: newCampaign.sentBy,
        sentAt: newCampaign.sentAt
      });
      setLeads(leads.map((lead) =>
        lead._id === newCampaign.leadId ? response.data.lead : lead
      ));
      setNewCampaign({
        leadId: leads[0]?._id || '',
        category: 'Promotional',
        subject: '',
        body: '',
        sentBy: role === 'admin' ? users[0]?._id || userId : userId,
        sentAt: ''
      });
      setError('');
      alert('Email campaign added');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add email campaign');
    }
  };

  const handleEditCampaign = async (leadId, campaignId) => {
    if (!editingCampaign.sentBy) {
      setError('Please select a user');
      return;
    }
    if (!editingCampaign.sentAt) {
      setError('Please select a date and time');
      return;
    }
    try {
      const response = await emailCampaignsAPI.updateEmailCampaign(leadId, campaignId, {
        category: editingCampaign.category,
        subject: editingCampaign.subject,
        content: editingCampaign.body,
        sentBy: editingCampaign.sentBy,
        sentAt: editingCampaign.sentAt
      });
      setLeads(leads.map((lead) =>
        lead._id === leadId ? response.data.lead : lead
      ));
      setEditingCampaign(null);
      setError('');
      alert('Email campaign updated');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update email campaign');
    }
  };

  const handleDeleteCampaign = async (leadId, campaignId) => {
    if (window.confirm('Are you sure you want to delete this email campaign?')) {
      try {
        const response = await emailCampaignsAPI.deleteEmailCampaign(leadId, campaignId);
        setLeads(leads.map((lead) => (lead._id === leadId ? response.data.lead : lead)));
        setError('');
        alert('Email campaign deleted');
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete email campaign');
      }
    }
  };

  const openHistoryModal = (lead, category) => {
    setSelectedLead(lead);
    setModalCategory(category);
    setShowModal(true);
  };

  const selectedLeadEmail =
    Array.isArray(leads) ? leads.find((lead) => lead._id === newCampaign.leadId)?.email || '' : '';

  // AI Generation handler
  const handleAIGenerate = async () => {
    setAILoading(true);
    setAISubject('');
    setAIBody('');
    const payload = {
      category: newCampaign.category,
      additionalContext: aiPrompt,
      leadEmail: selectedLeadEmail,
      createdBy: userId,
    };
    try {
      const res = await emailGeneratorAPI.generateEmail(payload);
      if (res.data.success) {
        setAISubject(res.data.subject || '');
        setAIBody(res.data.body || '');
      } else {
        setAISubject('AI generation failed.');
        setAIBody('');
      }
    } catch (e) {
      setAISubject('AI generation failed.');
      setAIBody('');
    }
    setAILoading(false);
  };

  const handleAIDone = () => {
    setNewCampaign((prev) => ({
      ...prev,
      subject: aiSubject,
      body: aiBody,
    }));
    setShowAIModal(false);
    setAISubject('');
    setAIBody('');
    setAIPrompt('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-6 mb-6">
          <div className="flex items-center space-x-4 mb-2">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-bold bg-blue-500 bg-clip-text text-transparent">
                Email Campaign Management
              </h2>
              <p className="text-gray-600 mt-1">Create and manage email campaigns for your leads</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}
        {leads.length === 0 && role !== 'admin' && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
            <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>No assigned leads available.</span>
          </div>
        )}

        {/* Add New Campaign Section */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-6 mb-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900">Create New Campaign</h3>
          </div>
          <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select
              value={newCampaign.leadId}
              onChange={(e) =>
                setNewCampaign((prev) => ({ ...prev, leadId: e.target.value }))
              }
              className="p-2 border rounded-lg text-sm sm:text-base w-full"
              disabled={leads.length === 0}
            >
              <option value="">Select Lead</option>
              {leads.map((lead) => (
                <option key={lead._id} value={lead._id}>
                  {lead.schoolName} ({lead.email})
                </option>
              ))}
            </select>
            <input
              type="text"
              value={selectedLeadEmail}
              readOnly
              placeholder="Lead email"
              className="p-2 border rounded-lg bg-gray-100 text-sm sm:text-base w-full"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={newCampaign.sentBy}
              onChange={(e) =>
                setNewCampaign((prev) => ({ ...prev, sentBy: e.target.value }))
              }
              className="p-2 border rounded-lg text-sm sm:text-base w-full"
              disabled={role !== 'admin'}
            >
              <option value="">Select User</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.username}
                </option>
              ))}
            </select>
            <select
              value={newCampaign.category}
              onChange={(e) =>
                setNewCampaign((prev) => ({ ...prev, category: e.target.value }))
              }
              className="p-2 border rounded-lg text-sm sm:text-base w-full"
            >
              <option value="Promotional">Promotional</option>
              <option value="Follow-up">Follow-up</option>
              <option value="Newsletter">Newsletter</option>
            </select>
            <input
              type="datetime-local"
              value={newCampaign.sentAt}
              onChange={(e) =>
                setNewCampaign((prev) => ({ ...prev, sentAt: e.target.value }))
              }
              className="p-2 border rounded-lg text-sm sm:text-base w-full"
            />
          </div>
          <input
            type="text"
            placeholder="Subject"
            value={newCampaign.subject}
            onChange={(e) =>
              setNewCampaign((prev) => ({ ...prev, subject: e.target.value }))
            }
            className="p-2 border rounded-lg text-sm sm:text-base w-full mt-2"
          />
          <div className="relative flex flex-col sm:flex-row gap-2">
            <textarea
              placeholder="Email body"
              value={newCampaign.body}
              onChange={(e) =>
                setNewCampaign((prev) => ({ ...prev, body: e.target.value }))
              }
              className="p-2 border rounded-lg text-sm sm:text-base w-full min-h-[120px]"
              rows="5"
              style={{ resize: 'vertical' }}
            />
          </div>
            <button
              type="button"
              onClick={() => setShowAIModal(true)}
              className="sm:w-auto w-full bg-purple-500 text-white mr-4 px-6 py-2 rounded-lg hover:bg-purple-600 transition"
            >
              Generate Content with AI
            </button>
          <button
            onClick={handleAddCampaign}
            className="w-full sm:w-auto bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition text-sm sm:text-base mt-3"
            disabled={leads.length === 0}
          >
            Add Campaign
          </button>
          </div>
        </div>
        {/* AI Modal */}
      <Modal isOpen={showAIModal} onClose={() => setShowAIModal(false)}>
        <h3 className="text-lg font-bold mb-4">Generate Email Content with AI</h3>
        <label className="block text-sm font-semibold mb-1">
          Give a description or context for your email:
        </label>
        <textarea
          value={aiPrompt}
          onChange={e => setAIPrompt(e.target.value)}
          className="w-full border rounded-lg p-2 mb-3 text-sm"
          rows={3}
          placeholder="Eg: Invitation for Olympiad platform"
        />
        <button
          className="bg-blue-600 text-white rounded-lg px-4 py-2 w-full hover:bg-blue-700 mb-3"
          onClick={handleAIGenerate}
          disabled={aiLoading || !aiPrompt}
        >
          {aiLoading ? 'Generating...' : 'Generate'}
        </button>
        {(aiSubject || aiBody) && (
          <>
            <label className="block text-xs font-semibold mb-1">Subject</label>
            <input
              value={aiSubject}
              onChange={e => setAISubject(e.target.value)}
              className="w-full border rounded-lg p-2 mb-2 text-sm"
            />
            <label className="block text-xs font-semibold mb-1">Body</label>
            <textarea
              value={aiBody}
              onChange={e => setAIBody(e.target.value)}
              rows={10}
              className="w-full border rounded-lg p-2 mb-2 text-sm"
            />
            <button
              className="bg-green-600 text-white px-4 py-2 rounded-lg w-full hover:bg-green-700"
              onClick={handleAIDone}
            >
              Done
            </button>
          </>
        )}
      </Modal>
        {/* Leads Listing */}
        {leads.map((lead) => (
          <div key={lead._id} className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-6 mb-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{lead.schoolName}</h3>
                <p className="text-gray-600">{lead.email}</p>
              </div>
            </div>
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-3 text-left">Category</th>
                  <th className="p-3 text-left">Subject</th>
                  <th className="p-3 text-left">Body</th>
                  <th className="p-3 text-left">Sent By</th>
                  <th className="p-3 text-left">Sent At</th>
                  <th className="p-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {lead.emailCampaigns.length > 0 ? (
                  lead.emailCampaigns.map((campaign) => (
                    <tr key={campaign._id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <button
                          onClick={() => openHistoryModal(lead, campaign.category)}
                          className="text-blue-500 hover:underline"
                        >
                          {campaign.category}
                        </button>
                      </td>
                      <td className="p-3 font-bold">{campaign.subject}</td>
                      <td className="p-3">
                        <div className="max-w-xs">
                          <p className="text-sm text-gray-700 line-clamp-2 overflow-hidden">
                            {campaign.content && campaign.content.length > 100 
                              ? `${campaign.content.substring(0, 100)}...` 
                              : campaign.content || 'No content'
                            }
                          </p>
                          {campaign.content && campaign.content.length > 100 && (
                            <button 
                              onClick={() => openHistoryModal(lead, campaign.category)}
                              className="text-blue-500 hover:text-blue-700 text-xs mt-1"
                            >
                              Read more
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="p-3">{campaign.sentBy?.username || 'Unknown'}</td>
                      <td className="p-3">{new Date(campaign.sentAt).toLocaleString()}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {(campaign.sentBy._id === userId || role === 'admin') && (
                            <button
                              onClick={() => setEditingCampaign({
                                id: campaign._id,
                                leadId: lead._id,
                                category: campaign.category,
                                subject: campaign.subject,
                                body: campaign.content,
                                sentBy: campaign.sentBy._id,
                                sentAt: new Date(campaign.sentAt).toISOString().slice(0, 16),
                              })}
                              className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition"
                            >
                              Edit
                            </button>
                          )}
                          {role === 'admin' && (
                            <button
                              onClick={() => handleDeleteCampaign(lead._id, campaign._id)}
                              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="p-3 text-center text-gray-500">
                      No email campaigns found for this lead.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {lead.emailCampaigns.length > 0 ? (
              lead.emailCampaigns.map((campaign) => (
                <div key={campaign._id} className="border rounded-lg p-4 bg-white shadow-sm">
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase">Category</span>
                      <button
                        onClick={() => openHistoryModal(lead, campaign.category)}
                        className="font-bold text-blue-500 hover:underline"
                      >
                        {campaign.category}
                      </button>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase">Subject</span>
                      <p className="font-bold">{campaign.subject}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-500 uppercase">Body</span>
                      <div className="mt-1">
                        <p className="text-sm text-gray-700 line-clamp-3">
                          {campaign.content && campaign.content.length > 150 
                            ? `${campaign.content.substring(0, 150)}...` 
                            : campaign.content || 'No content'
                          }
                        </p>
                        {campaign.content && campaign.content.length > 150 && (
                          <button 
                            onClick={() => openHistoryModal(lead, campaign.category)}
                            className="text-blue-500 hover:text-blue-700 text-xs mt-1"
                          >
                            Read more
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase">Sent By</span>
                        <p className="text-sm text-gray-700 mt-1">{campaign.sentBy?.username || 'Unknown'}</p>
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase">Sent At</span>
                        <p className="text-sm text-gray-700 mt-1">
                          {new Date(campaign.sentAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 pt-2">
                      {(campaign.sentBy._id === userId || role === 'admin') && (
                        <button
                          onClick={() => setEditingCampaign({
                            id: campaign._id,
                            leadId: lead._id,
                            category: campaign.category,
                            subject: campaign.subject,
                            body: campaign.content,
                            sentBy: campaign.sentBy._id,
                            sentAt: new Date(campaign.sentAt).toISOString().slice(0, 16),
                          })}
                          className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition text-sm"
                        >
                          Edit
                        </button>
                      )}
                      {role === 'admin' && (
                        <button
                          onClick={() => handleDeleteCampaign(lead._id, campaign._id)}
                          className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition text-sm"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 py-4">No email campaigns found for this lead.</p>
            )}
          </div>
          {/* Edit Campaign Inline Form */}
          {editingCampaign && editingCampaign.leadId === lead._id && (
            <div className="mt-6 border border-blue-200 rounded-lg p-4 bg-blue-50">
              <h4 className="text-base sm:text-lg font-semibold mb-4">Edit Campaign</h4>
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  <select
                    value={editingCampaign.category}
                    onChange={(e) =>
                      setEditingCampaign((prev) => ({
                        ...prev,
                        category: e.target.value,
                      }))
                    }
                    className="p-2 border rounded-lg text-sm sm:text-base w-full"
                  >
                    <option value="Promotional">Promotional</option>
                    <option value="Follow-up">Follow-up</option>
                    <option value="Newsletter">Newsletter</option>
                  </select>
                  <select
                    value={editingCampaign.sentBy}
                    onChange={(e) =>
                      setEditingCampaign((prev) => ({
                        ...prev,
                        sentBy: e.target.value,
                      }))
                    }
                    className="p-2 border rounded-lg text-sm sm:text-base w-full"
                    disabled={role !== 'admin'}
                  >
                    <option value="">Select User</option>
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.username}
                      </option>
                    ))}
                  </select>
                  <input
                    type="datetime-local"
                    value={editingCampaign.sentAt}
                    onChange={(e) =>
                      setEditingCampaign((prev) => ({
                        ...prev,
                        sentAt: e.target.value,
                      }))
                    }
                    className="p-2 border rounded-lg text-sm sm:text-base w-full"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Subject"
                  value={editingCampaign.subject}
                  onChange={(e) =>
                    setEditingCampaign((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  className="p-2 border rounded-lg text-sm sm:text-base w-full mt-2"
                />
                <textarea
                  value={editingCampaign.body}
                  onChange={(e) =>
                    setEditingCampaign((prev) => ({
                      ...prev,
                      body: e.target.value,
                    }))
                  }
                  className="p-2 border rounded-lg text-sm sm:text-base w-full min-h-[120px]"
                  rows="5"
                />
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() =>
                      handleEditCampaign(lead._id, editingCampaign.id)
                    }
                    className="flex-1 bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition text-sm sm:text-base"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setEditingCampaign(null)}
                    className="flex-1 bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        ))}
        {showModal && selectedLead && (
          <EmailHistoryModal
            lead={selectedLead}
            category={modalCategory}
            onClose={() => setShowModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default EmailCampaign;
